import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

export default function RealtimeAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const screens = [
    { x: 80, y: 120, label: 'Alice', delay: 1.8 },
    { x: 200, y: 160, label: 'Bob', delay: 2.0 },
    { x: 320, y: 120, label: 'Carol', delay: 2.2 },
  ];

  return (
    <div ref={ref} className="relative w-full h-72 md:h-80">
      <svg viewBox="0 0 400 220" className="w-full h-full">
        {/* Central server/actor */}
        <motion.g
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.5, type: 'spring' }}
        >
          {/* Isometric server */}
          <polygon
            points="200,30 240,50 200,70 160,50"
            fill="#3b82f6"
            stroke="#1d4ed8"
            strokeWidth="2"
          />
          <polygon
            points="200,70 240,50 240,75 200,95"
            fill="#2563eb"
            stroke="#1d4ed8"
            strokeWidth="2"
          />
          <polygon
            points="200,70 160,50 160,75 200,95"
            fill="#1d4ed8"
            stroke="#1e40af"
            strokeWidth="2"
          />
          {/* Broadcast icon */}
          <text x="200" y="60" textAnchor="middle" fontSize="10" fill="white" fontWeight="bold">HUB</text>
        </motion.g>

        {/* Broadcast waves */}
        {[0, 1, 2].map((i) => (
          <motion.circle
            key={i}
            cx="200"
            cy="60"
            r="20"
            fill="none"
            stroke="#60a5fa"
            strokeWidth="2"
            initial={{ r: 20, opacity: 0.8 }}
            animate={isInView ? { 
              r: [20, 60, 100], 
              opacity: [0.6, 0.3, 0],
            } : { r: 20, opacity: 0 }}
            transition={{
              duration: 2,
              delay: 0.8 + i * 0.5,
              repeat: Infinity,
              repeatDelay: 1,
            }}
          />
        ))}

        {/* Connection lines to screens */}
        {screens.map((screen, i) => (
          <motion.line
            key={`line-${i}`}
            x1="200"
            y1="80"
            x2={screen.x}
            y2={screen.y - 20}
            stroke="#d1d5db"
            strokeWidth="2"
            strokeDasharray="4 4"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 0.5, delay: 1 + i * 0.2 }}
          />
        ))}

        {/* User screens */}
        {screens.map((screen, i) => (
          <motion.g
            key={`screen-${i}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
            transition={{ delay: screen.delay, type: 'spring', stiffness: 200 }}
          >
            {/* Screen */}
            <rect
              x={screen.x - 35}
              y={screen.y}
              width="70"
              height="45"
              rx="4"
              fill="#f3f4f6"
              stroke="#9ca3af"
              strokeWidth="2"
            />
            {/* Screen content */}
            <rect
              x={screen.x - 30}
              y={screen.y + 5}
              width="60"
              height="25"
              rx="2"
              fill="white"
            />
            {/* Bid amount shown */}
            <motion.text
              x={screen.x}
              y={screen.y + 22}
              textAnchor="middle"
              fontSize="11"
              fill="#22c55e"
              fontWeight="bold"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: [0, 1] } : { opacity: 0 }}
              transition={{ delay: screen.delay + 0.5 }}
            >
              £150
            </motion.text>
            {/* Stand */}
            <rect
              x={screen.x - 10}
              y={screen.y + 45}
              width="20"
              height="8"
              fill="#9ca3af"
            />
            {/* User label */}
            <text
              x={screen.x}
              y={screen.y + 65}
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {screen.label}
            </text>
          </motion.g>
        ))}

        {/* "New bid!" notification appearing on all screens */}
        {screens.map((screen, i) => (
          <motion.g
            key={`notif-${i}`}
            initial={{ opacity: 0, y: -5 }}
            animate={isInView ? { opacity: [0, 1, 1, 0], y: [-5, 0, 0, -5] } : { opacity: 0 }}
            transition={{ 
              delay: 2.8, 
              duration: 2, 
              repeat: Infinity, 
              repeatDelay: 2,
            }}
          >
            <rect
              x={screen.x - 25}
              y={screen.y - 18}
              width="50"
              height="16"
              rx="8"
              fill="#22c55e"
            />
            <text
              x={screen.x}
              y={screen.y - 7}
              textAnchor="middle"
              fontSize="8"
              fill="white"
              fontWeight="bold"
            >
              New bid!
            </text>
          </motion.g>
        ))}

        {/* Tagline */}
        <motion.text
          x="200"
          y="210"
          textAnchor="middle"
          fontSize="12"
          fill="#374151"
          fontWeight="500"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 3 }}
        >
          Same update, same moment, everywhere
        </motion.text>
      </svg>
    </div>
  );
}
